import groupBy from 'lodash/groupBy';
import { ILine } from '../../../../../models/line.model';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import { IconMotif } from '../../Icon/icon-motif.enum';
import LineBadge from '../../LineBadge';
import Fact from '../Fact';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { ITrafficSituation } from '../../../../../models/traffic-situation.model';

// HELPER FUNCTIONS
function transportModeToIconMotif(transportMode: TransportMode): IconMotif {
  switch (transportMode) {
    case TransportMode.bus:
      return IconMotif.Bus;
    case TransportMode.bussondemand:
      return IconMotif.BussOnDemand;
    case TransportMode.car:
      return IconMotif.Car;
    case TransportMode.train:
      return IconMotif.Train;
    case TransportMode.ferry:
      return IconMotif.Ferry;
    case TransportMode.tram:
      return IconMotif.Tram;
    case TransportMode.taxi:
      return IconMotif.Taxi;
    case TransportMode.walk:
      return IconMotif.Walk;
    case TransportMode.bike:
      return IconMotif.Bike;
    default:
      return IconMotif.Info; // Default icon for unknown transport modes
  }
}

// MAIN COMPONENT
export default function LineListFact({ trafficSituation, className = '' }: { trafficSituation: ITrafficSituation; className?: string }): React.ReactNode | null {
  const distinctLines = TrafficSituationHelper.getDistinctLines(trafficSituation);

  if (distinctLines.length === 0) return null;

  const linesByTransportMode = groupBy(distinctLines, (line) => line.transportMode);

  return (
    <ul className={`list-unstyled ${className ?? ''}`}>
      {Object.entries(linesByTransportMode).map(([transportMode, transportModeLines]) => (
        <li key={transportMode}>
          {transportMode === TransportMode.train ? (
            <TrainsWithLineBadges lines={transportModeLines} />
          ) : (
            <TransportModeWithLineBadges transportMode={transportMode as TransportMode} lines={transportModeLines} />
          )}
        </li>
      ))}
    </ul>
  );
}

// HELPER COMPONENTS
// TODO: Add support for showTrainDirectionName, showNonTrainDirectionName, showAppliesFromDateOnFutureLines, directionAsStrong in line badges components

function TrainsWithLineBadges({ lines }: { lines: ILine[] }): React.ReactNode {
  return (
    <Fact iconMotif={IconMotif.Train}>
      <div className="last-child-margin-bottom-none">
        {lines.map((line) => (
          <div key={line.gid} className="d-flex align-items-center mb-1">
            <LineBadge line={line} className="me-1" />
            <span>{line.directionName}</span>
          </div>
        ))}
      </div>
    </Fact>
  );
}

function TransportModeWithLineBadges({ transportMode, lines }: { transportMode: TransportMode; lines: ILine[] }): React.ReactNode {
  return (
    <Fact iconMotif={transportModeToIconMotif(transportMode)}>
      {lines.map((line) => (
        <LineBadge key={line.gid} line={line} className="me-1" />
      ))}
    </Fact>
  );
}
